import { useRef, useState } from "react";
import type React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

/**
 * Source tooltip anchored near the cursor. Radix HoverCards position against
 * the trigger's bounding box; with a full-row-width trigger that lands the
 * card in the middle of the row, far from the pointer. This wrapper tracks
 * the pointer's x within the trigger while the card is closed and, on open,
 * feeds it to Radix as `alignOffset` with `side="top" align="start"` — so
 * the card pops just above the row, horizontally at the cursor. Collision
 * padding keeps it on-screen at row extremes.
 *
 * The `building-data-widget` class on the content is required so the `--bd-*`
 * theme tokens resolve inside the Radix portal.
 */
export function SourceHoverCard({
  trigger,
  triggerStyle,
  children,
}: {
  trigger: React.ReactNode;
  triggerStyle?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [alignOffset, setAlignOffset] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pointerXRef = useRef<number | null>(null);

  return (
    <HoverCard
      openDelay={120}
      closeDelay={200}
      open={open}
      onOpenChange={(next) => {
        if (next) {
          const rect = wrapRef.current?.getBoundingClientRect();
          if (rect && pointerXRef.current != null) {
            // Nudge the card's left edge slightly left of the cursor.
            setAlignOffset(Math.max(0, Math.round(pointerXRef.current - rect.left) - 16));
          }
        }
        setOpen(next);
      }}
    >
      <HoverCardTrigger asChild>
        <div
          ref={wrapRef}
          style={triggerStyle}
          onPointerMove={(e) => {
            if (!open) pointerXRef.current = e.clientX;
          }}
        >
          {trigger}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        alignOffset={alignOffset}
        collisionPadding={8}
        className="building-data-widget w-auto bg-background p-3"
      >
        {children}
      </HoverCardContent>
    </HoverCard>
  );
}
